import * as React from 'react'

import { cn } from '../cn'

export interface SvgIconProps
  extends Omit<React.SVGProps<SVGSVGElement>, 'color'> {
  /** Sizing shorthand matching the old MUI fontSize prop. */
  fontSize?: 'inherit' | 'small' | 'medium' | 'large'
  /**
   * Either an MUI-style semantic name (primary, success, info, warning,
   * error, secondary, action, disabled, inherit) or any valid CSS color.
   * Semantic names map to design tokens; raw CSS colors pass through.
   */
  color?: string
}

const sizeClasses: Record<NonNullable<SvgIconProps['fontSize']>, string> = {
  inherit: 'h-[1em] w-[1em]',
  small: 'h-5 w-5',
  medium: 'h-6 w-6',
  large: 'h-[35px] w-[35px]',
}

// The icon paints with fill="currentColor", so the color is whatever CSS
// `color` resolves to. Historically these were MUI `color="primary"` etc.
// props; after the MUI removal they were silently dropped (an invalid SVG
// `color` attribute), leaving icons at the inherited color — often invisible
// (e.g. white-on-white in light mode). Map the semantic names to text tokens
// here so every icon is fixed at the source; `inherit` intentionally keeps the
// ambient currentColor.
const semanticColorClasses: Record<string, string | undefined> = {
  inherit: undefined,
  primary: 'text-primary',
  secondary: 'text-text-secondary',
  success: 'text-success',
  info: 'text-info',
  warning: 'text-warning',
  error: 'text-error',
  action: 'text-text-secondary',
  disabled: 'text-text-disabled',
}

export function createSvgIcon(children: React.ReactNode, displayName: string) {
  const Icon = React.forwardRef<SVGSVGElement, SvgIconProps>(
    ({ fontSize = 'medium', className, color, ...props }, ref) => {
      const isSemantic = color != null && color in semanticColorClasses
      return (
        <svg
          ref={ref}
          viewBox="0 0 24 24"
          fill="currentColor"
          focusable="false"
          aria-hidden="true"
          // Pass raw CSS colors through; drop semantic names (handled by class).
          color={isSemantic ? undefined : color}
          className={cn(
            'inline-block shrink-0 select-none',
            sizeClasses[fontSize],
            isSemantic ? semanticColorClasses[color] : undefined,
            className,
          )}
          {...props}
        >
          {children}
        </svg>
      )
    },
  )
  Icon.displayName = `${displayName}Icon`
  return Icon
}
